/**
 /**
  * Generates a open funnel SQL query for ClickHouse,
  * supporting various operators for filter values through the 'op' field.
  *
  * @param steps - Array of step objects, each with a type and optional filter keys with {op, value} structure.
  * @param timeFilter - Number of days to look back or a date range object.
  * @returns SQL query string for open funnel analysis.
  */
export const getOpenFunnelQuery = (
  steps: Array<{ type: string; event: string; [key: string]: any }>,
  timeFilter: number | { from: Date; to: Date } = 30,
): string => {
  if (!steps || steps.length === 0) {
    return 'SELECT NULL AS event, 0 AS total WHERE 1=0';
  }

  // Generate the time filter condition
  let timeCondition: string;
  if (typeof timeFilter === 'number') {
    // Legacy support: number of days
    timeCondition = `timestamp >= today() - INTERVAL ${timeFilter} DAY AND timestamp <= today()`;
  } else {
    // Date range support
    const fmt = (d: Date) => d.toLocaleDateString('en-CA', { timeZone: 'UTC' });
    const fromDate = fmt(timeFilter.from);
    const toDate = fmt(timeFilter.to);
    timeCondition = `timestamp >= toDate('${fromDate}') AND timestamp <= toDate('${toDate}')`;
  }

  // Extract filter keys from all steps
  const filterKeys = steps.reduce((acc, step) => {
    Object.keys(step).forEach((key) => {
      if (key !== 'type' && key !== 'event' && !acc.includes(key)) {
        acc.push(key);
      }
    });
    return acc;
  }, [] as string[]);

  // Generate separate SELECT statements for each step (completely independent)
  const stepSelects = steps.map((step) => {
    const escapedType = escapeValue(step.type);
    const escapedEvent = escapeValue(step.event);

    // Build WHERE condition based on type
    let whereCondition: string;
    if (escapedType === 'event') {
      whereCondition = `type = '${escapedEvent}'`;
    } else {
      whereCondition = `type = '${escapedType}' AND event = '${escapedEvent}'`;
    }

    // Build step-specific filters
    const stepFilters = filterKeys
      .map((key) => {
        const filterValue = step[key]?.value;
        const op = step[key]?.op;
        if (filterValue !== undefined && op !== undefined) {
          return `AND ${generateFilterCondition(key, filterValue, op)}`;
        }
        return null;
      })
      .filter(Boolean);

    // Combine all conditions
    const allConditions = [timeCondition, whereCondition, ...stepFilters].join(
      '\n        AND ',
    );

    return `    SELECT 
        '${escapedEvent}' AS event,
        COUNT(DISTINCT anonymous_id) AS total
    FROM events
    WHERE ${allConditions}`;
  });

  // Generate ORDER BY CASE statement to maintain funnel step order
  const orderCases = steps
    .map(
      (step, index) =>
        `        WHEN '${escapeValue(step.event)}' THEN ${index + 1}`,
    )
    .join('\n');

  // Construct the final query using UNION ALL wrapped in a subquery
  return `SELECT event, total 
FROM (
${stepSelects.join('\n    UNION ALL\n')}
) AS combined_results
ORDER BY 
    CASE event
${orderCases}
    END`;
};

/**
 * Generates a closed funnel SQL query for ClickHouse,
 * supporting cascading filters where each filter from previous steps applies to all subsequent steps.
 * Uses equality for filter values (ignores 'op' field).
 *
 * @param steps - Array of step objects, each with a type and optional filter keys with {op, value} structure.
 * @param timeFilter - Number of days to look back or a date range object.
 * @returns SQL query string for closed funnel analysis.
 */
export const getClosedFunnelQuery = (
  steps: Array<{ type: string; event: string; [key: string]: any }>,
  timeFilter: number | { from: Date; to: Date } = 30,
): string => {
  if (!steps || steps.length === 0) {
    return 'SELECT NULL AS event, 0 AS total WHERE 1=0';
  }

  // Generate the time filter condition
  let timeCondition: string;
  if (typeof timeFilter === 'number') {
    // Legacy support: number of days
    timeCondition = `timestamp >= today() - INTERVAL ${timeFilter} DAY AND timestamp <= today()`;
  } else {
    // Date range support
    const fmt = (d: Date) => d.toLocaleDateString('en-CA', { timeZone: 'UTC' });
    const fromDate = fmt(timeFilter.from);
    const toDate = fmt(timeFilter.to);
    timeCondition = `timestamp >= toDate('${fromDate}') AND timestamp <= toDate('${toDate}')`;
  }

  // Extract filter keys and build cascading filter conditions
  const filterKeys = steps.reduce((acc, step) => {
    Object.keys(step).forEach((key) => {
      if (key !== 'type' && key !== 'event' && !acc.includes(key)) {
        acc.push(key);
      }
    });
    return acc;
  }, [] as string[]);

  // Build the funnel_data CTE with base filters
  let funnelDataQuery = `funnel_data AS (
    SELECT * 
    FROM events 
    WHERE ${timeCondition}`;

  // Add any global filters if they exist across all steps
  const globalFilters = filterKeys
    .map((key) => {
      // Check if all steps have the same value AND operator for this key
      const firstStep = steps[0];
      if (!firstStep) return null;

      const firstStepValue = firstStep[key]?.value;
      const firstStepOp = firstStep[key]?.op;
      if (
        firstStepValue !== undefined &&
        firstStepOp !== undefined &&
        steps.every(
          (step) =>
            step[key]?.value === firstStepValue &&
            step[key]?.op === firstStepOp,
        )
      ) {
        return `AND ${generateFilterCondition(key, firstStepValue, firstStepOp)}`;
      }
      return null;
    })
    .filter(Boolean);

  if (globalFilters.length > 0) {
    funnelDataQuery += '\n      ' + globalFilters.join('\n      ');
  }

  funnelDataQuery += '\n)';

  // Build step CTEs
  const stepCTEs: string[] = [];
  const tuples: string[] = [];

  steps.forEach((step, index) => {
    const stepNum = index + 1;
    const stepName = `step${stepNum}_users`;
    const escapedType = escapeValue(step.type);
    const escapedEvent = escapeValue(step.event);

    // Build filter conditions for this specific step
    const getStepFilters = (tableAlias = '') => {
      return filterKeys
        .map((key) => {
          const filterValue = step[key]?.value;
          const op = step[key]?.op;
          if (filterValue !== undefined && op !== undefined) {
            return `AND ${generateFilterCondition(key, filterValue, op, tableAlias)}`;
          }
          return null;
        })
        .filter(Boolean);
    };

    // Build WHERE condition based on type
    const getWhereCondition = (tableAlias = '') => {
      const prefix = tableAlias ? `${tableAlias}.` : '';
      if (escapedType === 'event') {
        return `${prefix}type = '${escapedEvent}'`;
      } else {
        return `${prefix}type = '${escapedType}' AND ${prefix}event = '${escapedEvent}'`;
      }
    };

    let stepQuery: string;
    if (index === 0) {
      // First step - select from funnel_data directly
      const stepFilters = getStepFilters();
      stepQuery = `${stepName} AS (
    SELECT DISTINCT anonymous_id
    FROM funnel_data
    WHERE ${getWhereCondition()}`;

      if (stepFilters.length > 0) {
        stepQuery += '\n      ' + stepFilters.join('\n      ');
      }
      stepQuery += '\n)';
    } else {
      // Subsequent steps - join with previous step
      const prevStepName = `step${index}_users`;
      const stepFilters = getStepFilters('fd');
      stepQuery = `${stepName} AS (
    SELECT DISTINCT fd.anonymous_id
    FROM funnel_data fd
    INNER JOIN ${prevStepName} s${index} ON fd.anonymous_id = s${index}.anonymous_id
    WHERE ${getWhereCondition('fd')}`;

      if (stepFilters.length > 0) {
        stepQuery += '\n      ' + stepFilters.join('\n      ');
      }
      stepQuery += '\n)';
    }

    stepCTEs.push(stepQuery);

    // Create tuple for arrayJoin
    tuples.push(
      `            tuple('${escapedEvent}', (SELECT COUNT(*) FROM ${stepName}))`,
    );
  });

  // Combine everything into the final query
  const allCTEs = [funnelDataQuery, ...stepCTEs].join(',\n\n');
  const arrayJoinTuples = tuples.join(',\n');

  return `WITH ${allCTEs}

SELECT 
    result.1 as event,
    result.2 as total
FROM (
    SELECT 
        arrayJoin([
${arrayJoinTuples}
        ]) AS result
)
ORDER BY 
    CASE event
${steps.map((step, index) => `        WHEN '${escapeValue(step.event)}' THEN ${index + 1}`).join('\n')}
    END ASC`;
};

/*
 * Returns a SQL query to get all unique funnel steps (event types) from the events table.
 * This is used to populate the dropdown for selecting funnel steps.
 */
export const getFunnelStepsQuery = () => {
  return "SELECT DISTINCT(event), type FROM events WHERE event != '' AND type IN ('track', 'decoded_log');";
};

/*
 * Returns the SQL operator for a given filter operation.
 * @param {string} op - The filter operation (e.g., 'equals', 'in', 'gt', etc.).
 * @returns {string} - The corresponding SQL operator.
 */
export const getOperator = (op: string): string => {
  switch (op) {
    case 'equals':
      return '=';
    case 'in': // Future support for IN operator
      return 'IN';
    case 'gt':
      return '>';
    case 'lt':
      return '<';
    case 'gte':
      return '>=';
    case 'lte':
      return '<=';
    default:
      return '=';
  }
};

/*
 * Escapes a value for use in SQL queries to prevent SQL injection.
 * Handles strings by escaping single quotes and converts other types to strings.
 * @param {any} value - The value to escape.
 * @returns {string} - The escaped value as a string.
 */
const escapeValue = (value: any): string => {
  if (typeof value === 'string') {
    // eslint-disable-next-line quotes
    return value.replace(/'/g, "''");
  }
  return String(value);
};

/*
 * Generates a filter condition for SQL queries based on the key, value, and operator.
 * Handles standard columns directly and custom properties using JSONExtractString for text values
 * and JSONExtractFloat for numeric values.
 * @param {string} key - The column or property key to filter on.
 * @param {string} value - The value to filter by.
 * @param {string} op - The operator to use for filtering (e.g., 'equals', 'in').
 * @param {string} [tableAlias=''] - Optional table alias for the column.
 * @returns {string} - The SQL filter condition string.
 */
const generateFilterCondition = (
  key: string,
  value: string,
  op: string,
  tableAlias = '',
): string => {
  const prefix = tableAlias ? `${tableAlias}.` : '';
  const escapedValue = escapeValue(value);

  // List of standard database columns that should be filtered directly
  const standardColumns = [
    'origin',
    'device',
    'browser',
    'os',
    'location',
    'referrer',
    'ref',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'version',
    'locale',
    'timezone',
  ];

  const parsedOp = getOperator(op);

  const isNumeric = (val: string): boolean => {
    return (
      !isNaN(Number(val)) && !isNaN(parseFloat(val)) && isFinite(Number(val))
    );
  };

  if (standardColumns.includes(key)) {
    return `${prefix}${key} ${parsedOp} '${escapedValue}'`;
  } else {
    if (isNumeric(value)) {
      const numericValue = Number(value);
      return `JSONExtractFloat(${prefix}properties, '${key}') ${parsedOp} ${numericValue}`;
    } else {
      // TODO: Future support for IN operator
      if (parsedOp === 'IN') {
        return `JSONExtractString(${prefix}properties, '${key}') ${parsedOp} ('${escapedValue}')`;
      } else {
        return `JSONExtractString(${prefix}properties, '${key}') ${parsedOp} '${escapedValue}'`;
      }
    }
  }
};
